import React, { useState } from 'react';
import { Box, Chip, Menu, MenuItem, CircularProgress } from '@mui/material';
import { Gavel, Lock, Send, EditNote } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthToken } from '../../lib/authToken';

export type ReviewStatus = 'draft' | 'attorney_reviewed' | 'sent' | 'locked';

const LABELS: Record<ReviewStatus, string> = {
  draft: 'Draft',
  attorney_reviewed: 'Attorney Reviewed',
  sent: 'Sent',
  locked: 'Locked',
};

const COLORS: Record<ReviewStatus, 'default' | 'info' | 'success' | 'warning'> = {
  draft: 'default',
  attorney_reviewed: 'info',
  sent: 'success',
  locked: 'warning',
};

const ICONS: Record<ReviewStatus, React.ReactElement> = {
  draft: <EditNote fontSize="small" />,
  attorney_reviewed: <Gavel fontSize="small" />,
  sent: <Send fontSize="small" />,
  locked: <Lock fontSize="small" />,
};

const TRANSITIONS: Record<ReviewStatus, ReviewStatus[]> = {
  draft: ['attorney_reviewed', 'locked'],
  attorney_reviewed: ['draft', 'sent', 'locked'],
  sent: ['locked', 'attorney_reviewed'],
  locked: ['attorney_reviewed'],
};

interface ReviewStatusChipProps {
  documentId: string;
  reviewStatus?: ReviewStatus | string | null;
  onChanged?: (next: ReviewStatus) => void;
  readOnly?: boolean;
}

const ReviewStatusChip: React.FC<ReviewStatusChipProps> = ({
  documentId,
  reviewStatus,
  onChanged,
  readOnly = false,
}) => {
  const { user } = useAuth();
  const current = (reviewStatus || 'draft') as ReviewStatus;
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [busy, setBusy] = useState(false);

  const isAttorney = user?.role === 'admin' || user?.role === 'lawyer';
  const canWrite = Boolean(user?.permissions?.['documents:write'] || user?.role === 'admin');

  const transition = async (next: ReviewStatus) => {
    setAnchor(null);
    setBusy(true);
    try {
      const res = await fetch(`/api/documents/${documentId}/review-status`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reviewStatus: next }),
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Failed to update review status'));
      }
      toast.success(`Marked as ${LABELS[next]}`);
      onChanged?.(next);
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Failed to update review status'));
    } finally {
      setBusy(false);
    }
  };

  const options = TRANSITIONS[current] || [];
  const interactive = !readOnly && canWrite && options.length > 0;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Chip
        size="small"
        icon={ICONS[current] || undefined}
        label={LABELS[current] || current}
        color={COLORS[current] || 'default'}
        onClick={interactive ? (e) => setAnchor(e.currentTarget) : undefined}
        sx={interactive ? { cursor: 'pointer' } : undefined}
      />
      {busy && <CircularProgress size={14} />}
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {options.map((next) => {
          const needsAttorney = next === 'attorney_reviewed' || next === 'locked';
          if (needsAttorney && !isAttorney) return null;
          return (
            <MenuItem key={next} onClick={() => transition(next)}>
              {LABELS[next]}
            </MenuItem>
          );
        })}
        {options.every((n) => (n === 'attorney_reviewed' || n === 'locked') && !isAttorney) && (
          <MenuItem disabled>Attorney required for next status</MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default ReviewStatusChip;
